/**
 * Sanitized HTML body for Impressum / Datenschutz.
 * Generators and existing sites publish these pages as HTML. The legal block
 * stores one fragment and still binds persona rows separately.
 */
import { isLegalProseTokenKey } from './fieldBindings';

/** Cap for a stored legal document (real privacy policies run well past 8k). */
export const LEGAL_BODY_HTML_MAX_CHARS = 80_000;

const ALLOWED = new Set(['h2', 'h3', 'h4', 'p', 'ul', 'ol', 'li', 'a', 'strong', 'em', 'br']);

const TITLE_LABEL: Record<'impressum' | 'datenschutz', RegExp> = {
  impressum: /^(impressum|imprint|rechtliche hinweise)$/i,
  datenschutz: /^(datenschutzerklärung|datenschutz|privacy policy|privacy)$/i,
};

export function escapeLegalHtmlText(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function decodeBasicEntities(value: string): string {
  let current = value;
  for (let pass = 0; pass < 2; pass += 1) {
    const next = current
      .replace(/&nbsp;/gi, ' ')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&#39;|&apos;/gi, "'")
      .replace(/&quot;/gi, '"')
      .replace(/&copy;/gi, '©')
      .replace(/&reg;/gi, '®')
      .replace(/&euro;/gi, '€')
      .replace(/&ndash;/gi, '–')
      .replace(/&mdash;/gi, '—')
      .replace(/&bull;/gi, '•')
      .replace(/&hellip;/gi, '…')
      .replace(/&#(\d+);/g, (_, n) => codePoint(Number(n)))
      .replace(/&#x([0-9a-f]+);/gi, (_, h) => codePoint(parseInt(h, 16)))
      .replace(/&amp;/gi, '&');
    if (next === current) break;
    current = next;
  }
  return current;
}

function codePoint(code: number): string {
  if (!Number.isFinite(code) || code < 0 || code > 0x10ffff) return '';
  try {
    return String.fromCodePoint(code);
  } catch {
    return '';
  }
}

function escapeAttr(value: string): string {
  return escapeLegalHtmlText(value).replaceAll('`', '&#96;');
}

function readHref(rawTag: string): string | null {
  const match = /\bhref\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+))/i.exec(rawTag);
  const href = decodeBasicEntities((match?.[1] ?? match?.[2] ?? match?.[3] ?? '').trim());
  if (!href || /[\u0000-\u001F]/.test(href)) return null;
  if (/^(javascript|data|vbscript):/i.test(href)) return null;
  if (/^(https?:|mailto:|tel:)/i.test(href)) return href;
  if (href.startsWith('/') && !href.startsWith('//') && !href.startsWith('/\\')) return href;
  if (href.startsWith('#') && !/^#javascript/i.test(href)) return href;
  return null;
}

function tagName(raw: string): { name: string; closing: boolean } | null {
  const match = /^<\s*(\/)?\s*([a-zA-Z][\w:-]*)/.exec(raw);
  if (!match?.[2]) return null;
  return { name: match[2].toLowerCase(), closing: Boolean(match[1]) };
}

/**
 * Allow headings, paragraphs, lists, emphasis, and safe links.
 * Unknown tags unwrap. Script, style, and event handlers are dropped.
 */
export function sanitizeLegalBodyHtml(input: string): string {
  return sanitizeLegalBodyHtmlInner(input, 0);
}

function sanitizeLegalBodyHtmlInner(input: string, depth: number): string {
  let html = input
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<(script|style|iframe|object|embed|form|svg|noscript|template|textarea)\b[\s\S]*?<\/\1>/gi, '')
    .replace(/<(script|style|iframe|object|embed|form|svg|noscript|template|textarea)\b[^>]*>/gi, '');

  html = html
    .replace(/<\s*(\/?)\s*h1\b/gi, '<$1h2')
    .replace(/<\s*(\/?)\s*h[56]\b/gi, '<$1h4');

  const out: string[] = [];
  const stack: string[] = [];
  const re = /<[^>]*>|[^<]+/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(html)) !== null) {
    const part = match[0];
    if (!part.startsWith('<')) {
      const text = escapeLegalHtmlText(decodeBasicEntities(part));
      if (text) out.push(text);
      continue;
    }
    const parsed = tagName(part);
    if (!parsed || !ALLOWED.has(parsed.name)) continue;
    if (parsed.name === 'br') {
      if (!parsed.closing) out.push('<br>');
      continue;
    }
    if (parsed.closing) {
      const idx = stack.lastIndexOf(parsed.name);
      if (idx < 0) continue;
      while (stack.length > idx) {
        const top = stack.pop();
        if (top) out.push(`</${top}>`);
      }
      continue;
    }
    if (parsed.name === 'a') {
      const href = readHref(part);
      if (!href) continue;
      stack.push('a');
      out.push(`<a href="${escapeAttr(href)}">`);
      continue;
    }
    stack.push(parsed.name);
    out.push(`<${parsed.name}>`);
  }
  while (stack.length > 0) {
    const top = stack.pop();
    if (top) out.push(`</${top}>`);
  }

  let result = out.join('').replace(/<(p|h2|h3|h4|li|strong|em|a)>\s*<\/\1>/gi, '');
  if (result.length <= LEGAL_BODY_HTML_MAX_CHARS) return result;
  const cut = result.slice(0, LEGAL_BODY_HTML_MAX_CHARS);
  const last = cut.lastIndexOf('>');
  const bounded = last > 0 ? cut.slice(0, last + 1) : '';
  if (!bounded || depth >= 2) return bounded;
  return sanitizeLegalBodyHtmlInner(bounded, depth + 1);
}

type LegalNode =
  | { type: 'text'; value: string }
  | { type: 'el'; name: string; href?: string; children: LegalNode[] };

/** A `#…` link only navigated the source page. It is not part of the document. */
function isFragmentHref(href: string | undefined): boolean {
  return Boolean(href?.startsWith('#'));
}

function nodeText(nodes: LegalNode[]): string {
  return nodes
    .map((node) => (node.type === 'text' ? decodeBasicEntities(node.value) : nodeText(node.children)))
    .join('');
}

function hasWords(text: string): boolean {
  return /[\p{L}\p{N}]/u.test(text);
}

/** A paragraph or item whose only content is in-page links (any label, any language). */
function onlyFragmentLinks(nodes: LegalNode[]): boolean {
  let sawFragment = false;
  for (const node of nodes) {
    if (node.type === 'text') {
      if (hasWords(decodeBasicEntities(node.value))) return false;
      continue;
    }
    if (node.name === 'br') continue;
    if (node.name === 'a' && isFragmentHref(node.href)) {
      sawFragment = true;
      continue;
    }
    if (node.name === 'strong' || node.name === 'em') {
      if (!onlyFragmentLinks(node.children)) return false;
      sawFragment = true;
      continue;
    }
    return false;
  }
  return sawFragment;
}

function parseLegalFragment(html: string): LegalNode[] {
  const roots: LegalNode[] = [];
  const stack: LegalNode[][] = [roots];
  const re = /<[^>]+>|[^<]+/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(html)) !== null) {
    const part = match[0];
    if (!part.startsWith('<')) {
      stack[stack.length - 1]?.push({ type: 'text', value: part });
      continue;
    }
    const parsed = tagName(part);
    if (!parsed || !ALLOWED.has(parsed.name)) continue;
    if (parsed.name === 'br') {
      stack[stack.length - 1]?.push({ type: 'el', name: 'br', children: [] });
      continue;
    }
    if (parsed.closing) {
      if (stack.length > 1) stack.pop();
      continue;
    }
    const element: LegalNode = { type: 'el', name: parsed.name, children: [] };
    if (parsed.name === 'a') {
      const href = /\bhref="([^"]*)"/i.exec(part)?.[1];
      if (href) element.href = decodeBasicEntities(href);
    }
    stack[stack.length - 1]?.push(element);
    stack.push(element.children);
  }
  return roots;
}

function serializeLegalNodes(nodes: LegalNode[]): string {
  return nodes.map(serializeLegalNode).join('');
}

function serializeLegalNode(node: LegalNode): string {
  if (node.type === 'text') return node.value;
  if (node.name === 'br') return '<br>';
  const inner = serializeLegalNodes(node.children);
  if (node.name === 'a') return `<a href="${escapeAttr(node.href ?? '')}">${inner}</a>`;
  return `<${node.name}>${inner}</${node.name}>`;
}

/**
 * Imported pages keep the source site's own navigation: a table of contents,
 * a link back to the top, and list items with no text. Those are structure,
 * not a list of phrases, so the same pass works for every site and language.
 * A bold-only paragraph is a heading the generator did not mark as one.
 */
export function tidyLegalDocumentHtml(html: string): string {
  return serializeLegalNodes(pruneLegalNodes(parseLegalFragment(html)));
}

function pruneLegalNodes(nodes: LegalNode[]): LegalNode[] {
  const next: LegalNode[] = [];
  for (const node of nodes) {
    if (node.type === 'text') {
      next.push(node);
      continue;
    }
    if (node.name === 'br') {
      next.push(node);
      continue;
    }
    if (node.name === 'a' && isFragmentHref(node.href)) {
      next.push(...pruneLegalNodes(node.children));
      continue;
    }
    if (node.name === 'p' || node.name === 'li') {
      if (onlyFragmentLinks(node.children) || !hasWords(nodeText(node.children))) continue;
      const children = pruneLegalNodes(node.children);
      const heading = headingFromBoldParagraph(node.name, children);
      next.push(heading ?? { type: 'el', name: node.name, children });
      continue;
    }
    const children = pruneLegalNodes(node.children);
    if ((node.name === 'ul' || node.name === 'ol') && !children.some((child) => child.type === 'el' && child.name === 'li')) {
      continue;
    }
    next.push({ type: 'el', name: node.name, href: node.href, children });
  }
  return next;
}

function headingFromBoldParagraph(name: string, children: LegalNode[]): LegalNode | null {
  if (name !== 'p' || children.length !== 1) return null;
  const only = children[0];
  if (!only || only.type !== 'el' || only.name !== 'strong') return null;
  if (only.children.some((child) => child.type === 'el')) return null;
  const label = nodeText(only.children).replace(/\s+/g, ' ').trim();
  if (label.length < 2 || label.length > 80) return null;
  return { type: 'el', name: 'h3', children: only.children };
}

/** Drop a leading page title the block already renders as its own h1. */
export function finalizeLegalDocumentHtml(
  html: string,
  kind: 'impressum' | 'datenschutz',
): string {
  const sanitized = tidyLegalDocumentHtml(sanitizeLegalBodyHtml(html));
  const leading = /^(\s*<h2>)([^<]{0,120})(<\/h2>)/i.exec(sanitized);
  if (!leading?.[2]) return sanitized;
  const label = leading[2].replace(/\s+/g, ' ').trim();
  if (!TITLE_LABEL[kind].test(label)) return sanitized;
  return sanitized.slice(leading[0].length).trim();
}

/** HTML ready to paint. Tidies imports that were stored before this pass. */
export function presentLegalBodyHtml(html: string): string {
  const trimmed = html.trim();
  if (!trimmed) return '';
  return tidyLegalDocumentHtml(sanitizeLegalBodyHtml(trimmed));
}

/** Plain crawl text (markdown-only pages) → one paragraph per line. */
export function legalPlainTextToHtml(text: string): string {
  const parts = text
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (parts.length === 0) return '';
  return sanitizeLegalBodyHtml(parts.map((line) => `<p>${escapeLegalHtmlText(line)}</p>`).join(''));
}

/** Review preview: block tags become line breaks, the rest is plain text. */
export function legalHtmlToReviewText(html: string): string {
  if (!html.includes('<')) return html.trim();
  return decodeBasicEntities(
    html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/(p|h\d|li|ul|ol|div)>/gi, '\n')
      .replace(/<[^>]+>/g, ' ')
      .replace(/[ \t]+\n/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .replace(/[ \t]{2,}/g, ' '),
  ).trim();
}

/**
 * Expand `@legalName` and the other prose tokens in text nodes only.
 * Persona values are escaped so a name like `Foo & Bar` cannot break markup.
 * Unknown `@…` sequences stay literal.
 */
export function expandLegalBodyHtmlTokens(
  html: string,
  tokens: Record<string, string>,
  missing: string,
): string {
  return html.replace(/(<[^>]+>)|([^<]+)/g, (full, tag: string | undefined, text: string | undefined) => {
    if (tag) return tag;
    return (text ?? '').replace(/@([a-zA-Z][a-zA-Z0-9-]*)/g, (match, key: string) => {
      if (!isLegalProseTokenKey(key)) return match;
      const value = tokens[key];
      const next = value && value !== missing ? value : missing;
      return escapeLegalHtmlText(next);
    });
  });
}
